import { type Harbour, describeKind } from '../core/harbours';

export interface HarbourPanelProps {
  harbour: Harbour;
  onClose: () => void;
}

/**
 * What a harbour marker opens.
 *
 * Its job is to get the skipper to the photographs and facility notes on
 * marinas.com without pretending this app knows anything it does not. Two
 * honesty problems are handled here rather than glossed over:
 *
 * - the position is a registry's, not a survey, and the chart underneath is
 *   still the only thing that says anything about depth;
 * - the link is matched by NAME. Where that match was not confident the link
 *   goes to a search instead, and the panel says which it is, because sending
 *   someone to a page about a different wharf is worse than making them pick.
 */
export function HarbourPanel({ harbour, onClose }: HarbourPanelProps): React.JSX.Element {
  const sourceName = harbour.source === 'dfo'
    ? 'Fisheries and Oceans Canada'
    : 'OpenStreetMap contributors';

  return (
    <div className="sheet sheet--harbour" role="dialog" aria-label={`Harbour: ${harbour.name}`}>
      <div className="sheet__header">
        <h2>{harbour.name}</h2>
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <p className="sheet__position">
        {describeKind(harbour)}
        {harbour.locality ? ` — ${harbour.locality}` : ''}
      </p>

      <a
        className="harbour__open"
        href={harbour.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {harbour.listed ? 'Photos and details on marinas.com' : 'Find it on marinas.com'}
      </a>

      {!harbour.listed && (
        <p className="sheet__hint sheet__hint--small">
          No listing matched this name confidently, so this opens a search
          rather than guessing at one that might be a different wharf.
        </p>
      )}

      <p className="sheet__hint sheet__hint--small">
        Opens marinas.com in a new tab, and needs a connection.
      </p>

      <p className="sheet__hint sheet__hint--small">
        Not chart data. The position is from {sourceName}, not a survey, and says
        nothing about the depth alongside — the chart under this marker is what
        does.
      </p>
    </div>
  );
}
